import React, { useEffect, useRef } from 'react';
import { renderAsync } from 'docx-preview';

import { Box } from '@mui/material';

import correctDocxLayout from './correctDocxLayout';

// docx-preview sets tab stops on a 500 ms timer after rendering, which fires late on a busy machine.
// They must be set while the pages are still off-screen at full size, before the correction, the swap
// and the shrink to fit; so wait until they are, not for a fixed time.
const TAB_STOPS_TIMEOUT_MS = 3000;

/**
 * Resolves once docx-preview has set the tab stops in the container. It sets them all in one pass,
 * turning each tab's em space into a non-breaking space, so one changed tab means they all are.
 */
const tabStopsSet = (container: HTMLElement): Promise<void> => {
    const tabs = Array.from(container.querySelectorAll('span')).filter(span => /-tab-stop$/.test(span.className));
    if (tabs.length === 0)
        return Promise.resolve();

    return new Promise(resolve => {
        const started = Date.now();
        const check = () => {
            if (tabs.some(tab => tab.textContent === ' ') || Date.now() - started > TAB_STOPS_TIMEOUT_MS)
                resolve();
            else
                setTimeout(check, 50);
        };
        check();
    });
}

interface DocxPreviewProps {
    docx: string;               // base64 .docx
    onError: (message: string) => void;
}

/**
 * Shows a .docx as pages. Each new version is rendered off-screen and then swapped in, so the
 * pages don't flash or jump back to the top while the user types. Pages shrink to fit the panel.
 */
const DocxPreview = (props: DocxPreviewProps) => {
    const visibleRef = useRef<HTMLDivElement>(null);
    const stagingRef = useRef<HTMLDivElement>(null);
    const latestRender = useRef(0);
    // the latest callback, without re-rendering the document when the parent passes a new one
    const onError = useRef(props.onError);
    onError.current = props.onError;
    const { docx } = props;

    useEffect(() => {
        const visible = visibleRef.current;
        const staging = stagingRef.current;
        if (!visible || !staging)
            return;

        const renderId = ++latestRender.current;
        const bytes = Uint8Array.from(atob(docx), c => c.charCodeAt(0));
        staging.replaceChildren();

        // experimental: real tab stops; without it every tab is one wide space and tabbed lines
        // (the "To:" line, the signature block) sit too far left
        renderAsync(bytes, staging, staging, { inWrapper: true, breakPages: true, renderHeaders: true, renderFooters: true, ignoreLastRenderedPageBreak: true, experimental: true })
            .then(() => tabStopsSet(staging))
            .then(() => {
                // a newer version started rendering meanwhile
                if (renderId !== latestRender.current)
                    return;
                correctDocxLayout(staging);
                visible.replaceChildren(...Array.from(staging.childNodes));
                fitToWidth(visible);
            })
            .catch(() => {
                if (renderId === latestRender.current)
                    onError.current('Could not display the document.');
            });
    }, [docx]);

    // shrink the pages when the panel is narrower than a page
    useEffect(() => {
        const visible = visibleRef.current;
        if (!visible)
            return;
        const observer = new ResizeObserver(() => fitToWidth(visible));
        observer.observe(visible);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <Box ref={visibleRef} />
            {/* rendered here first, out of sight, then moved into view */}
            <Box ref={stagingRef} sx={{ position: 'absolute', left: -100000, top: 0, visibility: 'hidden' }} aria-hidden />
        </>
    );
};

const fitToWidth = (container: HTMLElement) => {
    const wrapper = container.querySelector<HTMLElement>('.docx-wrapper');
    const page = container.querySelector<HTMLElement>('section.docx');
    if (!wrapper || !page)
        return;
    wrapper.style.setProperty('zoom', '1');
    const available = container.clientWidth;
    const needed = page.offsetWidth + 40;     // the wrapper's padding around the page
    wrapper.style.setProperty('zoom', needed > available ? String(available / needed) : '1');
}

export default DocxPreview;
